import { guided } from '@/llm/guidedRuntime';
import { KITS, KIT_IDS } from '@/webapp/runtime/kits';
import { refusalOf, SIGN_IN_TO_DESCRIBE_A_LOOK } from '@/components/wizard/wizardNotes';

/**
 * "Warmer." "Less corporate." "More like a magazine."
 *
 * The look step offers three kits and invites a reaction, and this turns that reaction into a
 * different three. It RE-RANKS; it cannot invent. The compile gate rejects any `S.kit(id)`
 * outside `KIT_IDS` (gate.ts:711), so a look conjured from feedback would be a preview of an
 * app that cannot be built — the one lie this step must not tell.
 *
 * It rides on the ordinary `/llm` proxy with no `structured` or `firstBuild` flag, so it runs
 * on the cheap model at the small cap: the task is picking three ids out of eight, and paying
 * build-model rates for it would be spending the person's credits on an opinion.
 */

/*
 * The sentence lives with the wizard's other notes, so the screen can say it BEFORE anybody
 * types without importing the model client to get it.
 */
export { SIGN_IN_TO_DESCRIBE_A_LOOK };

/**
 * What an account that cannot pay is told. Not "try again": the refusal repeats until the
 * balance changes. True whether the call is priced or merely needs a balance behind it.
 */
export const NO_CREDITS_TO_DESCRIBE_A_LOOK =
  'This account does not have the credits to describe a look. Pick one of these three instead.';

/**
 * A refusal for want of a session, from either client: the browser's `unauthorized` or 401,
 * or the phone's `GuidedLLMError('signup_required')` — see `refusalOf`.
 */
export function isSignInRefusal(err: unknown): boolean {
  return refusalOf(err) === 'signed-out';
}

/** A refusal for want of credits, from either client — see `refusalOf`. */
export function isCreditRefusal(err: unknown): boolean {
  return refusalOf(err) === 'no-credits';
}

export type KitFeedbackResult =
  | { kind: 'ranked'; kitRanking: string[] }
  | { kind: 'failed'; reason: string };

export interface RankKitsDeps {
  generate?: (input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
  }) => Promise<{ content: string }>;
}

const SYSTEM = [
  'You choose visual identities for a small web app. Answer with JSON only:',
  '{"kitRanking":["id","id","id"]}',
  '',
  'Choose ONLY from these kits — ids are fixed and nothing outside this list exists:',
  ...KITS.map((k) => `  ${k.id} — ${k.blurb} Suits: ${k.when}`),
  '',
  'Order them best-first for the app AND the feedback. Name three. No prose.',
].join('\n');

/** A JSON object out of a reply that may be fenced, prefaced or chatty. */
function parseRanking(content: string): string[] | null {
  const candidates = [
    content,
    /```(?:json)?\s*([\s\S]*?)```/.exec(content)?.[1] ?? '',
    /\{[\s\S]*\}/.exec(content)?.[0] ?? '',
  ];
  for (const text of candidates) {
    if (!text.trim()) continue;
    try {
      const parsed = JSON.parse(text) as { kitRanking?: unknown };
      if (Array.isArray(parsed.kitRanking)) {
        return parsed.kitRanking.filter(
          (id): id is string => typeof id === 'string' && KIT_IDS.includes(id),
        );
      }
    } catch {
      /* try the next shape */
    }
  }
  return null;
}

export async function rankKitsFromFeedback(
  idea: string,
  feedback: string,
  signedIn: boolean,
  deps: RankKitsDeps = {},
): Promise<KitFeedbackResult> {
  const said = feedback.trim();
  // Nothing was asked for, so nothing is spent. A blank box is not a request.
  if (!said) return { kind: 'failed', reason: 'Say what you would like changed.' };
  // Refused before anything is asked: the proxy would refuse it, and nothing should be spent.
  if (!signedIn) return { kind: 'failed', reason: SIGN_IN_TO_DESCRIBE_A_LOOK };

  let content: string;
  try {
    const generate = deps.generate ?? ((input) => guided().generate(input));
    const res = await generate({
      systemPrompt: SYSTEM,
      messages: [{ role: 'user', content: `App: ${idea}\nWhat they want: ${said}` }],
      signedIn,
    });
    content = res.content;
  } catch (err) {
    // A session that lapsed mid-wizard is not a hiccup; trying again cannot fix it.
    if (isSignInRefusal(err)) return { kind: 'failed', reason: SIGN_IN_TO_DESCRIBE_A_LOOK };
    // Nor is an empty balance: the proxy refuses even a free call with nothing behind it.
    if (isCreditRefusal(err)) return { kind: 'failed', reason: NO_CREDITS_TO_DESCRIBE_A_LOOK };
    return { kind: 'failed', reason: 'Could not change the looks just now. Try again.' };
  }

  const ranking = parseRanking(content);
  /*
   * An empty result is a failure, not a quiet no-op. Somebody typed a sentence and pressed a
   * button; leaving the three cards exactly as they were, with no word about why, is the
   * shape that reads as a dead button and makes people stop trusting the control.
   */
  if (!ranking?.length) {
    return { kind: 'failed', reason: 'That did not land on a look. Try saying it another way.' };
  }
  return { kind: 'ranked', kitRanking: ranking };
}
